import { useState } from 'react';
import { motion } from 'framer-motion';
import { Languages, ChevronDown, ChevronUp } from 'lucide-react';
import { UserAPI } from '@shared/types';
import { useT } from '@/lib/i18n';

interface LanguageSelectorProps {
  localLanguage: string;
  selectedLanguages: UserAPI.LanguagePreference[] | undefined;
  onLanguageSelect: (languages: UserAPI.LanguagePreference[] | undefined) => void;
}

export function LanguageSelector({
  localLanguage,
  selectedLanguages,
  onLanguageSelect,
}: LanguageSelectorProps) {
  const { t } = useT();
  const [showSelector, setShowSelector] = useState(true);

  const options: { value: UserAPI.LanguagePreference; label: string }[] = [
    { value: 'english', label: t('booking.preferences.options.language.english') },
    { value: 'local', label: localLanguage },
  ];

  const hasSelection = selectedLanguages && selectedLanguages.length > 0;
  const displayText = hasSelection
    ? selectedLanguages.map(v => options.find(o => o.value === v)?.label).filter(Boolean).join(', ')
    : null;

  const handleSelect = (value: UserAPI.LanguagePreference) => {
    const current = selectedLanguages ?? [];
    const isSelected = current.includes(value);

    if (isSelected) {
      const next = current.filter(v => v !== value);
      onLanguageSelect(next.length > 0 ? next : undefined);
    } else {
      onLanguageSelect([...current, value]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-6"
    >
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="w-full flex items-center justify-between my-2 group"
      >
        <div className="flex items-center">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${
            hasSelection ? 'bg-primary-500/20' : 'bg-dark-elevated dark:bg-neutral-800'
          }`}>
            <Languages className={`w-5 h-5 transition-colors ${
              hasSelection ? 'text-primary-500' : 'text-dark-muted dark:text-neutral-400'
            }`} />
          </div>
          <div className="text-left">
            <div className="text-xs text-dark-muted dark:text-neutral-400 mb-0.5">{t('booking.preferences.tableLanguage')}</div>
            <div className="text-sm font-semibold dark:text-neutral-100">
              {displayText ?? t('booking.preferences.tableLanguage')}
            </div>
          </div>
        </div>
        {hasSelection && (
          showSelector ? (
            <ChevronUp className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          ) : (
            <ChevronDown className="w-5 h-5 text-dark-muted dark:text-neutral-400 group-hover:text-primary-800 dark:group-hover:text-primary-400 transition-colors" />
          )
        )}
      </button>

      {(!hasSelection || showSelector) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <div className="grid grid-cols-2 gap-3">
            {options.map((option) => {
              const isSelected = selectedLanguages?.includes(option.value) ?? false;
              return (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`p-4 rounded-lg transition-all text-left ${
                    isSelected ? 'select-option-selected' : 'select-option'
                  }`}
                >
                  <div className="font-semibold text-sm dark:text-neutral-100">
                    {option.label}
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
